import React from 'react';
import { SessionTimerProps } from '../types';

const SessionTimer: React.FC<SessionTimerProps> = ({ 
  isActive = false, 
  onTimeUpdate, 
  showControls = false,
  className = '' 
}) => {
  const [sessionTime, setSessionTime] = React.useState(0);
  const [isRunning, setIsRunning] = React.useState(false);

  // Update isRunning when isActive prop changes
  React.useEffect(() => {
    setIsRunning(isActive);
  }, [isActive]);

  // Timer effect
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRunning) {
      interval = setInterval(() => {
        setSessionTime(prev => {
          const newTime = prev + 1;
          if (onTimeUpdate) {
            onTimeUpdate(newTime);
          }
          return newTime;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRunning, onTimeUpdate]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    setIsRunning(true);
  };

  const handleStop = () => {
    setIsRunning(false);
  };

  const handleReset = () => {
    setIsRunning(false);
    setSessionTime(0);
    if (onTimeUpdate) {
      onTimeUpdate(0);
    }
  };

  return (
    <div 
      className={className} 
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        gap: '1rem'
      }}
    >
      {/* Timer Display */}
      <div 
        style={{ 
          fontSize: '2rem', 
          fontWeight: 'bold',
          color: isRunning ? 'var(--success-color)' : 'var(--text-muted)',
          fontFamily: 'monospace',
          textAlign: 'center',
          padding: '1rem',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '0.75rem',
          border: `2px solid ${isRunning ? 'var(--success-color)' : 'var(--border-color)'}`,
          transition: 'all 0.3s ease',
          minWidth: '200px'
        }}
      >
        {formatTime(sessionTime)}
      </div>
      
      {/* Status Indicator */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '0.5rem',
        color: 'var(--text-secondary)',
        fontSize: '0.875rem'
      }}>
        <div 
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: isRunning ? 'var(--success-color)' : 'var(--text-muted)',
            animation: isRunning ? 'pulse 2s infinite' : 'none'
          }}
        ></div>
        <span>{isRunning ? 'Session Active' : 'Session Inactive'}</span>
      </div>
      
      {/* Control Buttons */}
      {showControls && (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={isRunning ? handleStop : handleStart}
            className={`btn ${isRunning ? 'btn-danger' : 'btn-primary'}`}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem'
            }}
          >
            {isRunning ? '⏸️ Pause' : '▶️ Start'}
          </button>
          
          <button
            onClick={handleReset}
            className="btn btn-secondary"
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem'
            }}
          >
            🔄 Reset
          </button>
        </div>
      )}

      {/* Add pulse animation styles */}
      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
            100% {
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default SessionTimer;